/**
 * Various products and associated metadata for the app
 * Match these to your payment processor products, MySQL: payments,
 * client: pages/signup.jsx, pages/meetings.jsx, and server side as applicable
 */

import productsData from "./products.json";

// Export the products array
export const products = productsData.products;

/**
 * Get a product by its plan type
 * @param {string} planType - The plan type to search for
 * @returns {Object|null} - The product object or null if not found
 */
export const getProductByPlanType = (planType) =>
  products.find((product) => product.plan_type === planType) || null;

/**
 * Get all products in a specific category
 * @param {string} category - The category to filter by
 * @returns {Array} - Array of products in the category
 */
export const getProductsByCategory = (category) =>
  products.filter((product) => product.category === category);

/**
 * Get the price for a specific plan type
 * @param {string} planType - The plan type to get the price for
 * @returns {number} - The price of the product or 0 if not found
 */
export const getPriceByPlanType = (planType) => {
  const product = getProductByPlanType(planType);
  return product ? product.price : 0;
};

/**
 * Get the linked table for a specific plan type
 * @param {string} planType - The plan type to get the linked table for
 * @returns {string|null} - The linked table or null if not found
 */
export const getLinkedTableByPlanType = (planType) => {
  const product = getProductByPlanType(planType);
  return product ? product.linked_table : null;
};

/**
 * Get the name for a specific plan type
 * @param {string} planType - The plan type to get the name for
 * @returns {string} - The name of the product or an empty string if not found
 */
export const getNameByPlanType = (planType) => {
  const product = getProductByPlanType(planType);
  return product ? product.name : "";
};

/**
 * Check if a plan is free (price is 0)
 * @param {string} planType - The plan type to check
 * @returns {boolean} - True if free, false otherwise
 */
export const isPlanFree = (planType) => getPriceByPlanType(planType) <= 0;

export default {
  products,
  getProductByPlanType,
  getProductsByCategory,
  getPriceByPlanType,
  getLinkedTableByPlanType,
  getNameByPlanType,
  isPlanFree,
};
